import { lazy, Suspense, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryRiskBadge } from '@/components/country-risk-badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { DEFAULT_PRODUCTION_PLACE_MAPTILER_TILE_PROVIDER_KEY } from '@/features/production-declaration/components/statement/production-place/production-place-maptiler-providers';
import { useLocalizedCountryName } from '@/hooks/use-localized-country';
import { useGetCountries } from '@/services/apis/queries/country/use-get-countries';
import type {
  ProductionPlace,
  ProductionPlaceCoordinates,
} from '@/types/core/production-declaration/production-place';

const ProductionPlaceVectorMapField = lazy(() =>
  import(
    '@/features/production-declaration/components/statement/production-place/production-place-vector-map-field'
  ).then((module) => ({ default: module.ProductionPlaceVectorMapField })),
);

type ProductionPlaceViewDialogProps = {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  productionPlace: ProductionPlace;
};

type ProductionPlaceDetailProps = {
  label: string;
  value: string | null | undefined;
};

function ProductionPlaceDetail({ label, value }: ProductionPlaceDetailProps) {
  return (
    <div className='space-y-2'>
      <dt className='font-medium text-[0.98rem] text-declaration-foreground tracking-tight'>
        {label}
      </dt>
      <dd className='flex min-h-12 items-center rounded-lg border border-declaration-border bg-declaration-hover px-4 text-declaration-foreground'>
        {value || '—'}
      </dd>
    </div>
  );
}

export function ProductionPlaceViewDialog({
  onOpenChange,
  open,
  productionPlace,
}: ProductionPlaceViewDialogProps) {
  const { t } = useTranslation('productionDeclaration');
  const { data: countries = [] } = useGetCountries();
  const { getLocalizedCountry } = useLocalizedCountryName(undefined, 'none');
  const country = countries.find(
    (item) => item.id === productionPlace.countryId,
  );
  const countryName = country?.isoCode
    ? (getLocalizedCountry(country.isoCode) ?? country.isoCode)
    : null;
  const handleMapChange = useCallback(
    (_coordinates: ProductionPlaceCoordinates) => undefined,
    [],
  );

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className='max-h-[94vh] overflow-y-auto rounded-[1.6rem] border border-declaration-border bg-white p-6 shadow-[0_18px_40px_rgba(17,24,39,0.12)] sm:max-w-[min(96vw,74rem)] lg:p-8'>
        <DialogHeader className='mb-5 text-left'>
          <DialogTitle className='font-semibold text-[1.85rem] text-declaration-foreground leading-none tracking-tight'>
            {t('materialCommodityOrigins.cards.productionPlace.viewModalTitle')}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            {t(
              'materialCommodityOrigins.cards.productionPlace.viewModalDescription',
            )}
          </DialogDescription>
        </DialogHeader>

        <dl className='grid gap-4 md:grid-cols-2'>
          <ProductionPlaceDetail
            label={t(
              'materialCommodityOrigins.cards.productionPlace.fields.description',
            )}
            value={productionPlace.description}
          />
          <ProductionPlaceDetail
            label={t(
              'materialCommodityOrigins.cards.productionPlace.fields.forestType',
            )}
            value={productionPlace.forestType?.label}
          />
          <div className='space-y-2'>
            <dt className='font-medium text-declaration-foreground'>
              {t(
                'materialCommodityOrigins.cards.productionPlace.fields.country',
              )}
            </dt>
            <dd className='flex flex-wrap items-center gap-2'>
              <span className='min-w-0 break-words'>
                {countryName ??
                  t('countryRisk.countryUnavailable', { ns: 'common' })}
              </span>
              <CountryRiskBadge
                context='recorded'
                risk={productionPlace.countryRiskLevel}
              />
            </dd>
          </div>
          <ProductionPlaceDetail
            label={t(
              'materialCommodityOrigins.cards.productionPlace.fields.timeRange',
            )}
            value={productionPlace.productionTimeRange}
          />
        </dl>

        {productionPlace.coordinates ? (
          <div className='mt-5'>
            <Suspense
              fallback={
                <Skeleton className='min-h-[28rem] w-full rounded-[1.4rem] lg:min-h-[34rem]' />
              }
            >
              <ProductionPlaceVectorMapField
                disabled
                onChange={handleMapChange}
                showInstructions={false}
                tileProviderKey={
                  DEFAULT_PRODUCTION_PLACE_MAPTILER_TILE_PROVIDER_KEY
                }
                value={productionPlace.coordinates}
              />
            </Suspense>
          </div>
        ) : null}

        <DialogFooter className='mt-6 pt-0'>
          <DialogClose asChild>
            <Button
              className='rounded-xl border-declaration-border px-5 text-declaration-foreground'
              type='button'
              variant='outline'
            >
              {t('materialCommodityOrigins.cards.productionPlace.close')}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
